import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Check, Package, CreditCard, Coins } from "lucide-react";

const Pricing = () => {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      
      <main className="flex-grow">
        <section className="bg-gradient-to-b from-blue-50 to-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl font-bold mb-6">Flexible Payment Options</h1>
              <p className="text-lg text-gray-600">
                Choose between pay-as-you-go or save with bulk credit purchases
              </p>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Pay Per Resume Option */}
              <div className="border rounded-xl shadow-sm overflow-hidden">
                <div className="bg-brand-500 text-white p-6 text-center">
                  <div className="mb-4 flex justify-center">
                    <CreditCard className="h-10 w-10" />
                  </div>
                  <h2 className="text-2xl font-bold">Pay Per Resume</h2>
                  <p className="mt-1">Perfect for one-time optimizations</p>
                </div>
                
                <div className="p-6 text-center">
                  <div className="mb-6">
                    <p className="text-5xl font-bold">$1</p>
                    <p className="text-gray-500">per optimized resume</p>
                  </div>
                  
                  <Link to="/auth/signup">
                    <Button size="lg" className="w-full mb-6">Try Now</Button>
                  </Link>
                  
                  <div className="space-y-4 text-left">
                    <div className="flex items-start gap-3">
                      <div className="bg-brand-100 p-1 rounded-full">
                        <Check className="h-4 w-4 text-brand-500" />
                      </div>
                      <p className="text-sm text-gray-600">No commitment required</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-brand-100 p-1 rounded-full">
                        <Check className="h-4 w-4 text-brand-500" />
                      </div>
                      <p className="text-sm text-gray-600">Pay only when you need it</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-brand-100 p-1 rounded-full">
                        <Check className="h-4 w-4 text-brand-500" />
                      </div>
                      <p className="text-sm text-gray-600">Instant access</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Credit Bundle Option */}
              <div className="border rounded-xl shadow-sm overflow-hidden">
                <div className="bg-brand-600 text-white p-6 text-center">
                  <div className="mb-4 flex justify-center">
                    <Coins className="h-10 w-10" />
                  </div>
                  <h2 className="text-2xl font-bold">Credit Bundles</h2>
                  <p className="mt-1">Best value for multiple applications</p>
                </div>
                
                <div className="p-6 text-center">
                  <div className="mb-6">
                    <p className="text-5xl font-bold">$20</p>
                    <p className="text-brand-500 font-semibold">Get 22 Credits</p>
                    <p className="text-gray-500">Save 10% on bulk purchase</p>
                  </div>
                  
                  <Link to="/auth/signup">
                    <Button size="lg" className="w-full mb-6">Try Now</Button>
                  </Link>
                  
                  <div className="space-y-4 text-left">
                    <div className="flex items-start gap-3">
                      <div className="bg-brand-100 p-1 rounded-full">
                        <Check className="h-4 w-4 text-brand-500" />
                      </div>
                      <p className="text-sm text-gray-600">Bonus credits on bulk purchases</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-brand-100 p-1 rounded-full">
                        <Check className="h-4 w-4 text-brand-500" />
                      </div>
                      <p className="text-sm text-gray-600">Credits valid for 12 months</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-brand-100 p-1 rounded-full">
                        <Check className="h-4 w-4 text-brand-500" />
                      </div>
                      <p className="text-sm text-gray-600">Track usage history</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-brand-100 p-1 rounded-full">
                        <Check className="h-4 w-4 text-brand-500" />
                      </div>
                      <p className="text-sm text-gray-600">Detailed purchase history</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="max-w-3xl mx-auto mt-16">
              <h3 className="text-xl font-bold mb-6 text-center">Frequently Asked Questions</h3>
              
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold mb-2">How do credits work?</h4>
                  <p className="text-gray-600">
                    Each credit can be used to optimize one resume. When you purchase credits in bulk, 
                    you'll receive bonus credits (e.g., 22 credits for $20). Credits are valid for 12 
                    months from the purchase date.
                  </p>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-2">Can I mix payment methods?</h4>
                  <p className="text-gray-600">
                    Yes! You can choose to pay $1 per resume or use credits for each optimization. 
                    If you run out of credits, you can always pay per resume or purchase more credits.
                  </p>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-2">Do credits expire?</h4>
                  <p className="text-gray-600">
                    Credits are valid for 12 months from the date of purchase. We'll notify you before 
                    your credits expire so you can use them in time.
                  </p>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-2">Can I get a refund for unused credits?</h4>
                  <p className="text-gray-600">
                    While credits are non-refundable, they are valid for 12 months, giving you plenty 
                    of time to use them for your job applications.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gray-50 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">
              Ready to Optimize Your Resume?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Choose the payment option that works best for you and start landing more interviews.
            </p>
            <Link to="/auth/signup">
              <Button size="lg">
                Get Started
              </Button>
            </Link>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Pricing;
